import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CircularProgress, Drawer, IconButton, Tooltip } from '@mui/material'
import { AlertCircle, ChevronRight, Paperclip, Plus, RefreshCw, Trash2, X } from 'lucide-react'
import Button from '../ui/Button'
import { showConfirmDeleteModal, showErrorModal, showSuccessModal } from '../ui/ConfirmDialog'
import { deleteCodeIndexAppendix, fetchCodeIndexAppendices } from '../../services/appendixService'
import { AppendixCodeItemOption, AppendixSummary } from '../../types/codeIndex'
import { getApiMessage } from '../../utils/apiMessage'
import AppendixEditorModal from './appendix/AppendixEditorModal'

export interface AppendixDrawerProps {
  open: boolean
  onClose: () => void
  codeIndexId?: string
  projectId?: string
  subtitle?: string
  canEdit?: boolean
  codeItems?: AppendixCodeItemOption[]
  createDisabledReason?: string
  onAppendixCreated?: (appendix: AppendixSummary) => void
  onAppendixUpdated?: (appendix: AppendixSummary) => void
  onAppendixDeleted?: (appendix: AppendixSummary) => void
}

// The editor is either creating a new appendix or showing an existing one.
type EditorState = { appendixId?: string } | null

const NO_CODE_ITEMS: AppendixCodeItemOption[] = []

function formatAppendixName(appendix: AppendixSummary): string {
  const title = appendix.title || 'Untitled appendix'
  return appendix.letter ? `${appendix.letter} — ${title}` : title
}

function formatAppendixSize(appendix: AppendixSummary): string {
  const columns = `${appendix.columnCount} ${appendix.columnCount === 1 ? 'column' : 'columns'}`
  const rows = `${appendix.rowCount} ${appendix.rowCount === 1 ? 'row' : 'rows'}`
  return `${columns} - ${rows}`
}

export const AppendixDrawer: React.FC<AppendixDrawerProps> = ({
  open,
  onClose,
  codeIndexId,
  projectId,
  subtitle,
  canEdit = false,
  codeItems = NO_CODE_ITEMS,
  createDisabledReason,
  onAppendixCreated,
  onAppendixUpdated,
  onAppendixDeleted,
}) => {
  const [appendices, setAppendices] = useState<AppendixSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [editor, setEditor] = useState<EditorState>(null)
  const latestRequestRef = useRef(0)

  const loadAppendices = useCallback(async (search: string) => {
    if (!codeIndexId) return

    const requestId = latestRequestRef.current + 1
    latestRequestRef.current = requestId
    setLoading(true)
    setError(null)
    try {
      const nextAppendices = await fetchCodeIndexAppendices(codeIndexId, { projectId, search })
      if (latestRequestRef.current !== requestId) return
      setAppendices(nextAppendices)
    } catch (err) {
      if (latestRequestRef.current !== requestId) return
      console.error('Failed to load appendices.', err)
      setError(getApiMessage(err) || 'Unable to load appendices.')
    } finally {
      if (latestRequestRef.current === requestId) {
        setLoading(false)
      }
    }
  }, [codeIndexId, projectId])

  useEffect(() => {
    if (!open) {
      setEditor(null)
      return
    }
    setSearchQuery('')
    setAppliedSearch('')
    void loadAppendices('')
  }, [loadAppendices, open])

  const handleSearch = () => {
    const nextSearch = searchQuery.trim()
    setAppliedSearch(nextSearch)
    void loadAppendices(nextSearch)
  }

  const handleDelete = async (appendix: AppendixSummary) => {
    if (!canEdit || deletingId || !codeIndexId) return

    const referenceWarning = appendix.referenceCount > 0
      ? ` Referenced by ${appendix.referenceCount} ${appendix.referenceCount === 1 ? 'item or value' : 'items or values'}; those references will be removed.`
      : ''
    const confirmed = await showConfirmDeleteModal({
      entityLabel: 'Appendix',
      title: 'Delete Appendix',
      description: `This action will remove "${formatAppendixName(appendix)}" and its tables.${referenceWarning} Do you want to proceed?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    })

    if (!confirmed) return

    setDeletingId(appendix.id)
    try {
      await deleteCodeIndexAppendix(codeIndexId, appendix.id, { projectId })
      await loadAppendices(appliedSearch)
      onAppendixDeleted?.(appendix)

      await showSuccessModal({
        title: 'Appendix Deleted',
        timer: 1200,
        showConfirmButton: false,
      })
    } catch (err) {
      console.error('Failed to delete appendix', err)
      await showErrorModal({
        title: 'Error',
        description: getApiMessage(err) || 'Failed to delete appendix. Please try again.',
        confirmText: 'OK',
      })
    } finally {
      setDeletingId(null)
    }
  }

  const handleAppendixSaved = (appendix: AppendixSummary) => {
    const wasUpdate = Boolean(editor?.appendixId)
    setEditor(null)
    void loadAppendices(appliedSearch)
    if (wasUpdate) {
      onAppendixUpdated?.(appendix)
    } else {
      onAppendixCreated?.(appendix)
    }
  }

  const createDisabled = !canEdit || Boolean(createDisabledReason) || !codeIndexId
  const createTooltip = createDisabledReason || (!canEdit ? 'You can view appendices but not add them.' : '')

  if (!open) return null

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: '34vw', minWidth: 380, maxWidth: 560, boxSizing: 'border-box' } }}
    >
      <div className="flex h-full flex-col bg-white">
        <div className="flex items-start justify-between border-b border-gray-200 px-4 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold text-gray-900">Appendices</span>
              {!canEdit ? (
                <span className="rounded-full bg-[#F1F5F9] px-2 py-0.5 text-xs font-medium text-gray-600">View only</span>
              ) : null}
            </div>
            {subtitle ? <div className="mt-1 truncate text-sm text-gray-500">{subtitle}</div> : null}
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <Tooltip title={createTooltip}>
              <span>
                <Button
                  type="button"
                  size="sm"
                  variant={createDisabled ? 'muted' : 'secondary'}
                  className={createDisabled ? '' : 'bg-[#EEF6FF]'}
                  onClick={() => setEditor({})}
                  disabled={createDisabled}
                  leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
                >
                  New
                </Button>
              </span>
            </Tooltip>
            <IconButton aria-label="Close appendices" onClick={onClose} size="small">
              <X className="h-5 w-5 text-[#1D5176]" aria-hidden="true" />
            </IconButton>
          </div>
        </div>

        <form
          className="flex items-center gap-2 border-b border-gray-200 px-4 py-3"
          onSubmit={(event) => {
            event.preventDefault()
            handleSearch()
          }}
        >
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Appendix name"
            aria-label="Appendix name"
            className="min-w-0 flex-1 rounded border border-[#CBD5E1] px-3 py-1.5 text-sm text-gray-700 outline-none focus:border-[#1D74D8]"
          />
          <Button type="submit" size="sm" variant="secondary" className="bg-[#EEF6FF]" disabled={loading}>
            Search
          </Button>
        </form>

        <div className="flex-1 overflow-y-auto">
          {loading && appendices.length === 0 ? (
            <div className="flex min-h-[240px] items-center justify-center">
              <CircularProgress size={24} />
            </div>
          ) : error ? (
            <div className="m-4 flex min-h-[200px] flex-col items-center justify-center gap-4 rounded-lg border border-red-200 bg-red-50 px-6 text-center">
              <AlertCircle className="h-8 w-8 text-red-500" aria-hidden="true" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-red-700">Unable to load appendices</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void loadAppendices(appliedSearch)}
                leftIcon={<RefreshCw className="h-4 w-4" aria-hidden="true" />}
              >
                Retry
              </Button>
            </div>
          ) : appendices.length === 0 ? (
            <div className="m-4 flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-gray-300 bg-[#FAFBFC] px-6 text-center">
              <div className="rounded-full bg-[#EEF6FF] p-3 text-[#1D5176]">
                <Paperclip className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-900">
                  {appliedSearch ? 'No matching appendices' : 'No appendices available'}
                </p>
                <p className="text-sm text-gray-500">
                  {appliedSearch
                    ? 'Try a different appendix name.'
                    : 'Appendices for this codesheet will be listed here.'}
                </p>
              </div>
            </div>
          ) : (
            <ul>
              {appendices.map((appendix) => {
                const isDeleting = deletingId === appendix.id

                return (
                  <li
                    key={appendix.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open ${formatAppendixName(appendix)}`}
                    onClick={() => setEditor({ appendixId: appendix.id })}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget) return
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        setEditor({ appendixId: appendix.id })
                      }
                    }}
                    className="flex cursor-pointer items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 hover:bg-[#F8FBFF] focus:bg-[#F8FBFF] focus:outline-none"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-base font-semibold text-gray-900">{formatAppendixName(appendix)}</p>
                      <p className="mt-0.5 text-sm text-gray-500">{formatAppendixSize(appendix)}</p>
                    </div>
                    <div className="flex flex-shrink-0 items-center">
                      {canEdit ? (
                        <Tooltip title="Delete appendix">
                          <span>
                            <IconButton
                              aria-label={`Delete ${formatAppendixName(appendix)}`}
                              onClick={(event) => {
                                event.stopPropagation()
                                void handleDelete(appendix)
                              }}
                              disabled={Boolean(deletingId)}
                              size="small"
                            >
                              {isDeleting ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Trash2 className="h-4 w-4 text-[#1D5176]" aria-hidden="true" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      {/* The whole row opens the appendix, so the chevron is only a visual cue. */}
                      <span className="flex h-[34px] w-[34px] items-center justify-center">
                        <ChevronRight className="h-5 w-5 text-[#1D5176]" aria-hidden="true" />
                      </span>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>

      {codeIndexId ? (
        <AppendixEditorModal
          open={Boolean(editor)}
          codeIndexId={codeIndexId}
          projectId={projectId}
          codeItems={codeItems}
          appendixId={editor?.appendixId}
          readOnly={Boolean(editor?.appendixId) && !canEdit}
          saveDisabledReason={createDisabledReason}
          onClose={() => setEditor(null)}
          onSaved={handleAppendixSaved}
        />
      ) : null}
    </Drawer>
  )
}

export default AppendixDrawer
